import styled from "@emotion/styled";
import SensorIcon from "components/gateways/SensorIcon";
import { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import {
    buildSparklineSeriesWithMissingGaps,
    CardSensorReading,
    getCardMetric,
    getCardSensorReading,
} from "service/proqueryService";
import { DeviceInfo } from "types";
import { UnknownDevice } from "constant";
import { ErrorOutlineOutlined } from "@mui/icons-material";
import {
    IconCircle,
    SensorContent,
    SensorLabel,
    SensorType,
    ReadingTimeText,
} from ".";
import { SparklineChart } from "./components/SparklineChart";
import { isBinarySensor } from "utils/functions";
import {
    getCurrentValueToDisplay,
    getReadingDateTimeToDisplay,
} from "utils/gatewayFunctions";

const CardContent = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
`;

const SensorInfo = styled.div`
    display: flex;
    flex-direction: column;
    width: calc(100% - 35px);
`;

const Value = styled.div<{ fontSize?: number | string }>`
    font-weight: 600;
    font-style: SemiBold;
    font-size: ${({ fontSize }) => fontSize ?? "18"}px;
    line-height: 100%;
    letter-spacing: 0%;
    text-align: right;
    color: #00cbaf;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
`;

// Props for the card
interface SensorCardProps {
    devices: DeviceInfo[];
    panel: any;
    widgetId: string;
}

export type CardMetric = {
    gatewayId: string;
    ldsuUId: string;
    said: number;
    value: [number, number][];
};

const MultiSensorCard = ({ devices, widgetId, panel }: SensorCardProps) => {
    const [dataCharts, setDataCharts] = useState<CardMetric[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const isMountedRef = useRef(true);
    const [readings, setReadings] = useState<
        Array<CardSensorReading & { gatewayId: string; deviceId: string }>
    >([]);

    const refreshRate = 30 * 1000; // milisesond
    const defaultAccuracy = 0;

    useEffect(() => {
        isMountedRef.current = true;
        const fetchChartData = async () => {
            if (!devices.length) {
                return;
            }
            const { queries } = panel;

            try {
                // chartMetric : This is used to show the graph
                const chartMetric = await getCardMetric({
                    queries: queries,
                });

                // resReadings : This is used to show the value
                // We can't use chartMetric as its average value and we need to show the last raw value
                // Update if above requirement is changed.
                const readingPromises = devices.map(
                    ({ gatewayId, deviceId, said, cls, format }) => {
                        return getCardSensorReading({
                            gatewayId,
                            deviceId,
                            said: Number(said),
                            cls,
                            format,
                        });
                    }
                );
                const resReadings = await Promise.all(readingPromises);

                const newReadings = resReadings.filter(
                    (r): r is NonNullable<typeof r> => r !== null
                );

                if (!isMountedRef.current) return;
                setReadings(newReadings);
                setDataCharts(chartMetric as any);
            } catch (err) {
                console.error(err);
            }
        };

        (async () => {
            await fetchChartData();
        })();

        const fetchMetricInterval = setInterval(async () => {
            await fetchChartData();
        }, refreshRate);

        return () => {
            isMountedRef.current = false;
            clearInterval(fetchMetricInterval);
        };
    }, [devices, refreshRate, panel]);

    const getReadingValue = (
        gatewayId: string,
        deviceId: string,
        said: number,
        accuracy: number | null,
        unit: string
    ) => {
        const readingData = readings.find(
            (d) =>
                d.said === Number(said) &&
                d.gatewayId === gatewayId &&
                d.deviceId === deviceId
        );

        if (
            readingData?.value === null ||
            readingData?.value === undefined ||
            readingData?.lastActive === null ||
            readingData?.lastActive === undefined
        ) {
            return "-";
        }

        return getCurrentValueToDisplay(
            readingData.value,
            readingData.lastActive,
            Number(accuracy ?? defaultAccuracy),
            unit
        );
    };

    const getReadingDateTime = (
        gatewayId: string,
        deviceId: string,
        said: number
    ) => {
        const readingData = readings.find(
            (d) =>
                d.said === Number(said) &&
                d.gatewayId === gatewayId &&
                d.deviceId === deviceId
        );
        return getReadingDateTimeToDisplay(readingData?.lastActive ?? null);
    };

    const renderSensor = () => {
        return (
            <div className="d-flex flex-column flex-fill">
                {devices.map(
                    (
                        {
                            sensorType,
                            deviceId,
                            said,
                            sensorName,
                            accuracy,
                            chartColor,
                            unit,
                            gatewayId,
                            cls,
                            format,
                        },
                        i
                    ) => {
                        const isBinary = isBinarySensor(cls, format);
                        const chartPoints =
                            dataCharts.find(
                                (d) =>
                                    d.said === Number(said) &&
                                    d.gatewayId === gatewayId &&
                                    d.ldsuUId === deviceId
                            )?.value ?? [];
                        const sparklineSeries = sensorName
                            ? buildSparklineSeriesWithMissingGaps({
                                  points: chartPoints,
                                  color: chartColor,
                                  isBinaryChart: isBinary,
                                  accuracy: accuracy ?? defaultAccuracy,
                              })
                            : [];
                        return (
                            <Row
                                className="mb-3 align-items-center"
                                // className="d-flex mb-3 justify-content-between align-items-center"
                                key={`${widgetId}-${deviceId}-${said}`}
                            >
                                <Col xs={4} md={5} lg={4}>
                                    <SensorContent>
                                        <IconCircle size={35} className="mr-2">
                                            {sensorName ? (
                                                <SensorIcon
                                                    sensorType={sensorType}
                                                    cls={cls}
                                                />
                                            ) : (
                                                <ErrorOutlineOutlined
                                                    color="error"
                                                    fontSize="large"
                                                />
                                            )}
                                        </IconCircle>

                                        <SensorInfo>
                                            <SensorLabel
                                                fontSize={16}
                                                className="text-truncate"
                                            >
                                                {sensorName ?? UnknownDevice}
                                            </SensorLabel>
                                            <SensorType
                                                fontSize={14}
                                                className="text-truncate"
                                            >
                                                {sensorType}
                                            </SensorType>
                                        </SensorInfo>
                                    </SensorContent>
                                </Col>
                                <Col xs={3} md={3} lg={4}>
                                    {sensorName &&
                                        sparklineSeries.length > 0 && (
                                            <SparklineChart
                                                gapSplitSeries={sparklineSeries}
                                                color={chartColor}
                                            />
                                        )}
                                </Col>
                                <Col xs={5} md={4} lg={4}>
                                    <div
                                        className="d-flex flex-column align-items-end text-end"
                                        style={{ minWidth: 0 }}
                                    >
                                        <Value>
                                            {getReadingValue(
                                                gatewayId,
                                                deviceId,
                                                said,
                                                accuracy,
                                                unit
                                            )}
                                        </Value>
                                        <ReadingTimeText
                                            fontSize={14}
                                            className="text-truncate mt-1"
                                            style={{ maxWidth: "100%" }}
                                        >
                                            {getReadingDateTime(
                                                gatewayId,
                                                deviceId,
                                                said
                                            )}
                                        </ReadingTimeText>
                                    </div>
                                </Col>
                            </Row>
                        );
                    }
                )}
            </div>
        );
    };

    return (
        <CardContent ref={containerRef} className="mt-2 mt-sm-0">
            {renderSensor()}
        </CardContent>
    );
};

export default MultiSensorCard;
